import { QuickRefer, QuickReferPayload } from "./types/QuickRefer.types";
import { config } from "../../config/env";
import { Result } from "@badrap/result";

export class QuickReferService {
  private apiKey: string;
  private getReferralByIdWebhookUrl: string;
  private getReferralsWebhookUrl: string;
  private saveReferralWebhookUrl: string;

  constructor() {
    this.apiKey = config.n8n?.apiKey || "";
    this.getReferralByIdWebhookUrl =
      config.n8n?.getReferralByIdWebhookUrl || "";
    this.getReferralsWebhookUrl = config.n8n?.getReferralsWebhookUrl || "";
    this.saveReferralWebhookUrl = config.n8n?.saveReferralWebhookUrl || "";

    if (
      !this.getReferralByIdWebhookUrl ||
      !this.saveReferralWebhookUrl ||
      !this.getReferralsWebhookUrl
    ) {
      // QuickRefer webhook URLs not configured
    }
  }

  async getReferralById(
    pas: string,
    applicationId: string
  ): Promise<Result<QuickRefer | null, Error>> {
    try {
      const encodedPas = encodeURIComponent(pas);
      const encodedApplicationId = encodeURIComponent(applicationId);
      const webhookUrl = `${this.getReferralByIdWebhookUrl}?pas=${encodedPas}&applicationId=${encodedApplicationId}`;
      const response = await fetch(webhookUrl, {
        method: "GET",
        headers: {
          Accept: "application/json",
          "X-API-Key": this.apiKey,
        },
      });

      if (!response.ok) {
        if (response.status === 404) {
          return Result.ok(null);
        }
        return Result.err(
          new Error(
            `Failed to check submission: HTTP ${response.status}. ${response.statusText}`
          )
        );
      }

      const resultText: string = await response.text();
      try {
        const resultData = JSON.parse(resultText);
        return Result.ok(resultData);
      } catch (parseError) {
        return Result.err(
          new Error("Response is not JSON or could not be parsed")
        );
      }
    } catch (error) {
      return Result.err(
        error instanceof Error ? error : new Error(String(error))
      );
    }
  }

  async saveReferral(
    payload: QuickReferPayload
  ): Promise<Result<QuickRefer, Error>> {
    try {
      const response = await fetch(this.saveReferralWebhookUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-API-Key": this.apiKey,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok && response.status !== 201) {
        return Result.err(
          new Error(
            `Failed to save submission: HTTP ${response.status}. ${response.statusText}`
          )
        );
      }

      const resultText: string = await response.text();
      try {
        const resultData = JSON.parse(resultText);
        return Result.ok(resultData);
      } catch (parseError) {
        return Result.err(
          new Error("Response is not JSON or could not be parsed")
        );
      }
    } catch (error) {
      if (
        error instanceof TypeError &&
        error.message.includes("Failed to fetch")
      ) {
        return Result.err(
          new Error(
            "Network error while saving submission. Please check your connection and try again."
          )
        );
      }
      return Result.err(
        error instanceof Error ? error : new Error(String(error))
      );
    }
  }

  async getReferrals(pas: string): Promise<Result<QuickRefer[], Error>> {
    try {
      const encodedPas = encodeURIComponent(pas);
      const webhookUrl = `${this.getReferralsWebhookUrl}?pas=${encodedPas}`;
      const response = await fetch(webhookUrl, {
        method: "GET",
        headers: {
          Accept: "application/json",
          "X-API-Key": this.apiKey,
        },
      });

      if (!response.ok) {
        if (response.status === 404) {
          return Result.ok([]);
        }
        const errorText = await response.text();
        return Result.err(
          new Error(
            `Failed to fetch submissions for ${pas}: HTTP ${response.status}. ${response.statusText}. Error: ${errorText}`
          )
        );
      }

      const resultText: string = await response.text();
      try {
        const resultData = JSON.parse(resultText);

        if (Array.isArray(resultData)) {
          return Result.ok(resultData);
        } else if (resultData && typeof resultData === "object") {
          return Result.ok([resultData]);
        } else {
          return Result.ok([]);
        }
      } catch (parseError) {
        return Result.err(
          new Error("Response is not JSON or could not be parsed")
        );
      }
    } catch (error) {
      return Result.err(
        error instanceof Error ? error : new Error(String(error))
      );
    }
  }
}

// Export singleton instance
export const quickReferService = new QuickReferService();
